import assetCategoryService from "../../../../services/assetService/assetCategoryService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETCATEGORIES } from "../../../../utils/constants";
import { AssetCategoryInterface } from "../../../../models/AssetModel/AssetCategory";

interface UpdateAssetCategoryContext {
  previousAssetCategory: AssetCategoryInterface[];
}

const useUpdateAssetCategory = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetCategoryInterface,
    Error,
    AssetCategoryInterface,
    UpdateAssetCategoryContext
  >({
    mutationFn: async (assetCategory: AssetCategoryInterface) => {
      // Assuming the ID is stored in the ticket object or can be derived
      const { id, ...assetCategoryData } = assetCategory;
      return assetCategoryService.Update(id || "", assetCategoryData);
    },

    onMutate: async (updatedAssetCategory: AssetCategoryInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_ASSETCATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousAssetCategory =
        queryClient.getQueryData<AssetCategoryInterface[]>([
          CACHE_KEY_ASSETCATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<AssetCategoryInterface[]>(
        [
          CACHE_KEY_ASSETCATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetCategory = []) =>
          assetCategory.map((assetCategory) =>
            assetCategory.id === updatedAssetCategory.id
              ? updatedAssetCategory
              : assetCategory
          )
      );

      return { previousAssetCategory };
    },

    onSuccess: (savedAssetCategory) => {
      queryClient.setQueryData<AssetCategoryInterface[]>(
        [
          CACHE_KEY_ASSETCATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetCategory) =>
          assetCategory?.map((assetCategory) =>
            assetCategory.id === savedAssetCategory.id
              ? savedAssetCategory
              : assetCategory
          )
      );
    },

    onError: (error, updatedAssetCategory, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetCategoryInterface[]>(
        [
          CACHE_KEY_ASSETCATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousAssetCategory
      );
    },
  });
};

export default useUpdateAssetCategory;
